import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Condo } from '@api/model/condo';
import { catchError, debounceTime, tap } from 'rxjs/operators';
import { CondoOperationTypes, CondoOperationTypesLabels } from '@api/model/condo';
import { merge, noop, of, Subject } from 'rxjs';
import { generateCsv, generateReport } from '../../../util/report-generator';
import { EcondosQuery } from '@api/model/query';
import { CondoService } from '@api/service/condo.service';
import { SessionService } from '../../../service/session.service';
import { ResidenceService } from '@api/service/residences.service';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface TableField {
  value: string;
  label: string;
}

@Component({
  selector: 'app-condos-page',
  templateUrl: './condos-page.component.html',
  styleUrls: ['./condos-page.component.scss']
})
export class CondosPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  condos: any = [];
  nzPageSize: number;
  nzPageIndex: number;
  totalData: any;
  fields: TableField[];

  private generalSearchParameter: string = '';
  private generalSearchValue: string = '';
  private searchWhitelabelValue: string = '';
  private searchPlanValue: string = '';
  currentSortData: { field: string; order: 'asc' | 'desc' | '' } = { field: 'name', order: 'asc' };

  generalSearchOptions: { label: string; value: string }[];
  planOptions: { label: string; value: string }[] = [];
  showDeleted = false;
  CondoOperationTypes = CondoOperationTypes;
  CondoOperationTypesLabels = CondoOperationTypesLabels;
  operationTypeOptions = Object.entries(CondoOperationTypesLabels).map(([value, label]) => ({ value, label }));
  searchOperationTypeValues: string[] = [];
  searchCustomOperationTypeValue: string = '';

  private customOperationTypeSearch$ = new Subject<string>();

  get showCustomOperationTypeInput(): boolean {
    return this.searchOperationTypeValues.includes(CondoOperationTypes.OTHER);
  }

  constructor(
    private condoService: CondoService,
    private sessionService: SessionService,
    private residenceService: ResidenceService,
    private notification: NzNotificationService
  ) {
    this.nzPageSize = 50;
    this.nzPageIndex = 0;
    this.fields = [
      { value: 'name', label: 'Nome' },
      { value: 'address', label: 'Localização' },
      { value: 'whiteLabel', label: 'WhiteLabel' },
      { value: 'cnpj', label: 'CNPJ' },
      { value: '_id', label: 'ID' },
      { value: 'createdAt', label: 'Data de Criação' },
      { value: 'residences', label: 'Unidades' },
      { value: 'plan', label: 'Plano' }
    ];
    this.generalSearchOptions = [
      {
        value: 'name',
        label: 'Nome'
      },
      {
        value: 'id',
        label: 'Id'
      },
      {
        value: 'cnpj',
        label: 'CNPJ'
      }
    ];
    this.planOptions = [
      { value: 'FREE', label: 'FREE / TRIAL' },
      { value: 'PREMIUM', label: 'PREMIUM' }
    ];
  }

  ngOnInit(): void {
    this.customOperationTypeSearch$.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getData());
    this.getData(this.currentSortData.field);
  }

  getData(sortBy?: string) {
    const query: EcondosQuery = {
      $select: 'name cnpj createdAt plan address city state daysOfTrial whiteLabel operationType customOperationType',
      $populate: [{ path: 'whiteLabel', select: 'name' }],
      $limit: this.nzPageSize,
      $page: this.nzPageIndex
    };

    if (sortBy) {
      if (this.currentSortData.field === sortBy) {
        this.currentSortData.order === 'asc' ? (this.currentSortData.order = 'desc') : (this.currentSortData.order = 'asc');
      } else {
        this.currentSortData.field = sortBy;
        this.currentSortData.order = 'asc';
      }
    } else {
      this.currentSortData = { field: 'createdAt', order: 'desc' };
    }

    if (this.generalSearchParameter === 'name') {
      query.name = { $regex: this.generalSearchValue, $options: 'i' };
    }
    if (this.generalSearchParameter === 'cnpj') {
      query.cnpj = Number(this.generalSearchValue);
    }
    if (this.generalSearchParameter === 'id') {
      query._id = this.generalSearchValue;
    }

    if (this.searchPlanValue) {
      query['plan.name'] = this.searchPlanValue;
    }
    if (this.searchWhitelabelValue) {
      query['whiteLabel'] = this.searchWhitelabelValue;
    }
    if (this.searchOperationTypeValues.length) {
      if (this.searchCustomOperationTypeValue) {
        query['operationType'] = { $in: [CondoOperationTypes.OTHER] };
      } else {
        query['operationType'] = { $in: this.searchOperationTypeValues };
      }
    }
    if (this.showCustomOperationTypeInput && this.searchCustomOperationTypeValue) {
      query['customOperationType'] = { $regex: this.searchCustomOperationTypeValue, $options: 'i' };
    }

    query.$sort = `${this.currentSortData.order === 'desc' ? '-' : ''}${this.currentSortData.field}`;

    let request;
    if (this.showDeleted) {
      query.$select = query.$select + ' deletedBy deletedAt';
      query.$populate?.push({
        path: 'deletedBy',
        select: 'firstName lastName'
      });
      request = this.condoService.getDeletedCondos(query);
    } else {
      request = this.condoService.getCondosWithCount(query);
    }

    request.subscribe({
      next: ({ data: condos, count }) => {
        this.condos = condos;
        this.totalData = Number(count);
      },
      error: err => {
        console.log(err);
      }
    });
  }

  refreshIndexChange(value: any) {
    this.nzPageIndex = --value;
    this.getData();
  }

  refreshSizeChange(value: any) {
    this.nzPageSize = value;
    this.getData();
  }

  async exportAsPDF(fields: TableField[], condos: Condo[]) {
    const data = this.handleData(condos);
    try {
      await generateReport('Lista_De_Condominios', fields, data, this.sessionService.user);
    } catch {
      this.notification.error('Relatório', 'Não foi possível gerar o PDF. Tente novamente.');
    }
  }

  exportAsCsv(fields: TableField[], condos: Condo[]) {
    const data = this.handleData(condos);
    const encodeUri = generateCsv(fields, data);
    const link = document.createElement('a');
    link.setAttribute('href', encodeUri);
    link.setAttribute('download', `Lista_De_Condominios.csv`);
    link.click();
  }

  handleData(condos: Condo[]) {
    return condos.map(condo => ({
      ...condo,
      createdAt: new Date(condo.createdAt).toLocaleDateString(),
      plan: condo.plan?.name || '',
      whiteLabel: condo?.whiteLabel?.name || 'Nenhum'
    }));
  }

  handleGeneralSearchFilter(data: any) {
    const { value, parameter } = data;
    this.generalSearchValue = value;
    this.generalSearchParameter = parameter;
    this.getData();
  }

  closeGeneralSearchFilter() {
    this.generalSearchValue = '';
    this.generalSearchParameter = '';
    this.getData();
  }

  handleSearchPlanFilter(value: string) {
    this.searchPlanValue = value;
    this.getData();
  }

  closeSearchPlanFilter() {
    this.searchPlanValue = '';
    this.getData();
  }

  handleWhitelabelFilter(value: string) {
    this.searchWhitelabelValue = value;
    this.getData();
  }

  closeWhitelabelFilter() {
    this.searchWhitelabelValue = '';
    this.getData();
  }

  compareDates(a: Condo, b: Condo) {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  }

  getSortedData(field: string) {
    this.currentSortData.field = field;
    this.getData(field);
  }

  countResidencesData(condoId: string) {
    this.residenceService.getResidencesCount(condoId).subscribe({
      next: count => {
        let condo: any = this.condos.find((condo: any) => condo._id === condoId);
        if (condo) {
          condo.residences = count || 0;
        }
      },
      error: err => {
        console.log(err);
      }
    });
  }

  countAllCondosResidences(condos: Condo[]) {
    const requests = condos.map((condo: any) =>
      this.residenceService.getResidencesCount(condo._id).pipe(
        tap(count => (condo.residences = count || 0)),
        catchError(e => of(null))
      )
    );
    merge(...requests, 3).subscribe({ next: noop, error: noop });
  }

  toggleShowDeleted() {
    this.showDeleted = !this.showDeleted;
    this.getData();
  }

  restoreCondo(condoId: string, index: number) {
    this.condoService.restoreCondo(condoId).subscribe({
      next: () => {
        this.condos.splice(index, 1);
        this.condos = [...this.condos];
        this.notification.success('Sucesso', 'Condomínio restaurado com sucesso');
      },
      error: err => {
        this.notification.error('Erro', 'Não foi possível restaurar o condomínio');
        console.log(err);
      }
    });
  }

  handleOperationTypeFilter(values: string[]) {
    this.searchOperationTypeValues = values;
    if (!values.includes(CondoOperationTypes.OTHER)) {
      this.searchCustomOperationTypeValue = '';
    }
    this.getData();
  }

  handleCustomOperationTypeFilter(value: string) {
    this.searchCustomOperationTypeValue = value;
    this.customOperationTypeSearch$.next(value);
  }

  closeOperationTypeFilter() {
    this.searchOperationTypeValues = [];
    this.searchCustomOperationTypeValue = '';
    this.getData();
  }
}
